// --- release4/X --- EXP-2 "Export CSV": bulk `export_csv` → `job_id` (kind `export_csv`), polled like the zip job.
import type { Job } from '@/lib/api/types';
import { api, req } from '@/lib/api/client';
import { JobFailedError, pollJob, saveUrl, type PollJobOptions } from '@/lib/api/jobs';
import { isApiError } from '@/lib/api/errors';
import { dismissToast, toast, updateToast } from '@/stores/ui';

const PROGRESS_TOAST_MS = 60 * 60_000;
const READY_TOAST_MS = 60_000;

type BulkResult = { succeeded: string[]; failed: { asset_id: string; code: string; message?: string }[]; job_id?: string | null };

/** Start the metadata CSV job for `ids` (VIEW per asset; the api reports the ids it refused) and resolve with the job id. */
export async function startCsvExport(wsId: string, ids: string[]): Promise<{ jobId: string | null; failed: number }> {
  const r = (await req(api.POST('/workspaces/{workspaceId}/assets/bulk', { params: { path: { workspaceId: wsId } }, body: { asset_ids: ids, action: 'export_csv' } as never }))) as BulkResult;
  return { jobId: r.job_id ?? null, failed: r.failed.length };
}

/** One toast follows the job: queued → running N% → ready with a Download action (`<a download>`, sync with the gesture). */
export async function trackCsvJob(fetchJob: () => Promise<Job>, opts: { count?: number; signal?: AbortSignal; poll?: PollJobOptions } = {}): Promise<Job | null> {
  const what = opts.count ? `${opts.count} asset${opts.count === 1 ? '' : 's'}` : 'your selection';
  const id = toast({ kind: 'info', title: 'Preparing CSV…', body: `Exporting the metadata of ${what}.`, durationMs: PROGRESS_TOAST_MS });
  try {
    const job = await pollJob(fetchJob, {
      ...opts.poll,
      signal: opts.signal,
      onProgress: (j) => updateToast(id, { title: j.state === 'running' ? `Exporting… ${Math.round(j.progress)}%` : 'Preparing CSV…', body: j.state === 'queued' ? 'Waiting for a worker.' : `Exporting the metadata of ${what}.` }),
    });
    const url = job.result?.url ?? null;
    dismissToast(id);
    if (!url) {
      toast({ kind: 'error', title: 'CSV finished without a download link' });
      return job;
    }
    toast({ kind: 'success', title: 'CSV ready', body: `${what} · link valid for 24 h`, durationMs: READY_TOAST_MS, action: { label: 'Download', onClick: () => saveUrl(url, job.result?.filename ?? 'lumen-export.csv') } });
    return job;
  } catch (e) {
    dismissToast(id);
    if (e instanceof DOMException && e.name === 'AbortError') return null;
    toast({ kind: 'error', title: 'CSV export failed', body: e instanceof JobFailedError ? e.message : isApiError(e) ? e.message : e instanceof Error ? e.message : undefined });
    return null;
  }
}

/** Button handler: start the job, report refused ids, then follow it to the download toast. */
export async function exportSelectionCsv(wsId: string, ids: string[]): Promise<void> {
  try {
    const { jobId, failed } = await startCsvExport(wsId, ids);
    if (failed) toast({ kind: 'error', title: `${failed} of ${ids.length} left out`, body: 'You cannot view those assets; the CSV covers the rest.' });
    if (!jobId) {
      if (!failed) toast({ kind: 'error', title: 'The server did not start a CSV export' });
      return;
    }
    await trackCsvJob(() => req(api.GET('/jobs/{jobId}', { params: { path: { jobId } } })), { count: ids.length - failed });
  } catch (e) {
    toast({ kind: 'error', title: isApiError(e) ? e.message : 'CSV export failed' });
  }
}
